import {
  BarcodeInspectionType,
  DayFormatEnum,
  ExpirationLogic,
  HourFormatEnum,
  InternalLabelArea,
  MonthFormatEnum,
  NotationOrderEnum,
  YearFormatEnum,
} from '@ulysses-inc/harami_api_client'
import { useCallback } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useQueryLabelAreaNames } from 'src/features/labelMaster/api/useQueryLabelNames'
import { usePreservedAreaSettings } from '../usePreservedAreaSettings'
import * as converter from './converter'

type UseAreaFormParams = {
  editingArea?: InternalLabelArea
  onAreaSaved: (area: InternalLabelArea) => void
}

export const useAreaForm = ({
  editingArea,
  onAreaSaved,
}: UseAreaFormParams) => {
  const { data: areaNameHistories = [] } = useQueryLabelAreaNames()
  const { preservedSettings, updateSettings } = usePreservedAreaSettings()

  const form = useForm<AreaFormValues>({
    defaultValues: converter.toFormValues({
      area: editingArea,
      preservedSettings,
    }),
    mode: 'onSubmit',
  })

  const handleSave = useCallback(
    (values: AreaFormValues) => {
      // 保存のタイミングで、一致タイプ=賞味期限の設定を保存する
      if (values.type === 'expiration') {
        updateSettings({
          expirationYear: values.expirationYear,
          expirationMonth: values.expirationMonth,
          expirationDay: values.expirationDay,
          expirationHour: values.expirationHour,
          expirationNotationOrder: values.expirationNotationOrder,
        })
      }
      onAreaSaved(converter.toArea(values))
    },
    [onAreaSaved, updateSettings],
  )

  // watch() は React Compiler と非互換のため、useWatch を使用する。
  // useWatch の戻り型は DeepPartialSkipArrayKey<T>（全フィールドが optional）だが、
  // defaultValues を必ず渡しているためランタイムでは常に完全な値が保証される。
  const formValues = useWatch({ control: form.control }) as AreaFormValues

  return {
    control: form.control,
    formValues,
    setValue: form.setValue,
    handleSubmit: form.handleSubmit,
    handleSave,
    areaNameHistories,
    preservedSettings,
  }
}

export type AreaType =
  | 'pattern'
  | 'alphanumeric'
  | 'alphanumericInput'
  | 'barcode'
  | 'expiration'

export type AreaFormValues = {
  id: string
  name: string
  type: AreaType
  x: number
  y: number
  width: number
  height: number

  alphanumericValue: string

  barcodeValue: string
  barcodeInspectionType: BarcodeInspectionType

  expirationLogic: ExpirationLogic
  expirationYear: YearFormatEnum
  expirationMonth: MonthFormatEnum
  expirationDay: DayFormatEnum
  expirationHour: HourFormatEnum
  expirationNotationOrder: NotationOrderEnum
  expirationDuration: {
    years: number
    months: number
    days: number
    hours: number
  }
}

export const AREA_FORM_DEFAULT_VALUES: AreaFormValues = {
  id: '',
  name: '',
  type: 'pattern',
  x: 0,
  y: 0,
  width: 0,
  height: 0,

  alphanumericValue: '',

  barcodeValue: '',
  barcodeInspectionType: BarcodeInspectionType.MATCH,

  expirationLogic: ExpirationLogic.BASE_TIME,
  expirationYear: YearFormatEnum.YYYY,
  expirationMonth: MonthFormatEnum.MM,
  expirationDay: DayFormatEnum.DD,
  expirationHour: HourFormatEnum.NONE,
  expirationNotationOrder: NotationOrderEnum.YMDH,
  expirationDuration: { years: 0, months: 0, days: 0, hours: 0 },
}
